import { defineComponent as _defineComponent } from 'vue'
import { computed, ref } from 'vue';
import axios from 'axios';
import config from '@/config/ConfigProvider';
import eventBus from '@/foundations/services/eventBus';
import FileUploader from '@/components/epubguide/FileUploader.vue';
import IsbnInput from '@/components/book/IsbnInput.vue';
import type { BookV1, NewBookV1 } from '@ilteducation/content-registry-types';
import { getLMGBaseInfo } from '@/foundations/helpers/epubGenerator';
import useUploadMedia from '@/comp/use-upload-media';
import { defaultSubscriptions } from '@/foundations/helpers/defaultSubscriptions';
import type { Category, Language } from '@ilteducation/content-ref-data-types';
import { getAllRefData } from '@/data-provider/refDataProvider';
import {
  CreateEpubRequest,
  GetCSVUploadLinkRequest,
  GetCSVUploadLinkResponse,
  ValidateCsvRequest,
  ValidateCsvResponse,
} from '@ilteducation/epub-guide-generator-types';


export default /*#__PURE__*/_defineComponent({
  __name: 'CreateNewEpubForm',
  setup(__props) {

const selectedGuideLanguages = ref<Language[]>([]);
const pushToCR = ref(false);
const selectedFile = ref<File | undefined>();
const selectedCover = ref<File | undefined>();
const isUploading = ref(false);
const isbn = ref<string>('');
const metadataFetched = ref(false);

const NATIVE_LANGUAGE_GUIDE_CATEGORY_ID =
  process.env.VUE_APP_ENVIRONMENT === 'staging' ? 'RQCTtPrNigNXJGDSV7ClM' : '7F4J48ZJtrftw967TwLRv'; // Läromedelsguide på modersmål

const BOOK = ref<BookV1 | NewBookV1>({
  availabilityRealms: ['se'],
  type: 'BookV1',
  isbn: '',
  realm: 'se',
  categories: [NATIVE_LANGUAGE_GUIDE_CATEGORY_ID],
  properties: {
    sentenceHighlighting: true,
    reflowable: true,
    pageProgressionDirection: 'ltr',
    pictureInPicture: [],
    additionalContent: {
      kahoot: false,
      teachingMaterial: true,
      externalMaterial: false,
    },
    frog: false,
    isEasyToRead: false,
    isFactBook: false,
    isTeachingMaterial: true,
    isSpeakingBook: false,
    isPictureBook: false,
    isBoardBook: false,
    isChapterBook: false,
    isShortStory: false,
    bookType: 'audio-book',
  },
  title: '',
  publisher: {
    id: '',
    name: '',
  },
  royaltyBeneficiaries: {
    id: '',
    name: '',
  },
  languages: {
    mainLanguage: '',
    text: undefined,
  },
  authors: [],
  illustrators: [],
  translators: [],
  keywords: [],
  pages: 0,
  duration: 0,
  description: '',
  accessRules: {
    services: [],
    subscriptionType: [],
  },
  serie: undefined,
  themas: undefined,
});

const rules = {
  isbn: [(value: string) => /^[0-9a-zA-Z-]*$/.test(value) || 'ISBN should only consist of numbers, letters and dashes'],
  required: (value: unknown) => !!value || 'This field is required',
};

const hasService = computed(() => (service: string) => BOOK.value.accessRules?.services.includes(service));

const availableSubscriptions = computed(() =>
  hasService.value('manabua')
    ? defaultSubscriptions.filter(
        (st) => st.value.startsWith('immigrant') || BOOK.value.accessRules.subscriptionType.includes(st.value),
      )
    : defaultSubscriptions,
);

const availableCategories = ref([] as Category[]);
const availableLanguages = ref([] as Language[]);
getAllRefData('languages').then((languages) => {
  availableLanguages.value = languages.sort((a: Language, b: Language) => a.language.localeCompare(b.language));
});

const shownCategories = computed(() => {
  if (BOOK.value.accessRules.subscriptionType) {
    return availableCategories.value
      .filter((cat) => cat.realms?.includes(BOOK.value.realm))
      .filter((categoryInRealm) => {
        let showCat = false;
        categoryInRealm.subscriptionTypes?.forEach((catSub) => {
          BOOK.value.accessRules.subscriptionType.forEach((itemSubType) => {
            if (catSub === itemSubType) {
              showCat = true;
            }
          });
        });
        // Allow manabua native language guides to also select Inlästa categories
        if (
          BOOK.value.accessRules.services.includes('manabua') &&
          categoryInRealm.subscriptionTypes?.includes('dyslexia')
        ) {
          showCat = true;
        }
        // If the category is already selected we still want it shown
        if (BOOK.value.categories?.some((bookCategory) => bookCategory === categoryInRealm.id)) showCat = true;
        return showCat;
      });
  }
  return availableCategories;
});

getAllRefData('categories').then((categories) => {
  availableCategories.value = categories.sort((a: Category, b: Category) => a.category.localeCompare(b.category));
});

const fetchingMetadata = ref(false);
const populateMetadata = (metadata?: Partial<NewBookV1>) => {
  if (metadata) {
    BOOK.value = { ...BOOK.value, ...metadata };
    metadataFetched.value = true;
  }
  fetchingMetadata.value = false;
};

const fileChanged = (file: File | undefined) => {
  selectedFile.value = file;
};

const coverChanged = (file: File | undefined) => {
  selectedCover.value = file;
};

const validateCSV = async () => {
  const data: ValidateCsvRequest = {
    fileName: selectedFile.value!.name.replace('.csv', ''),
  };
  return axios.post(`${config.value('epubGuideGeneratorUrl')}/validateCSV`, data);
};

const uploadCsv = async (languages: string[]) => {
  try {
    isUploading.value = true;
    const data: GetCSVUploadLinkRequest = {
      languages,
      fileName: selectedFile.value!.name.replace('.csv', ''),
      pushToCR: pushToCR.value,
    };
    const res = await axios.post(`${config.value('epubGuideGeneratorUrl')}/getCsvUploadLink`, data);
    if (res.data.type !== 'SuccessResponseV1') {
      console.error('Failed to get upload link');
      eventBus.$emit('snackbar.open', 'Error uploading file');
      return;
    }
    const { uploadLink }: GetCSVUploadLinkResponse = res.data.payload;

    await fetch(uploadLink, {
      method: 'PUT',
      body: selectedFile.value,
      headers: {
        'Content-Type': 'text/csv',
      },
    });
  } catch (error) {
    console.error(error);
    eventBus.$emit('snackbar.open', 'Error uploading file');
  } finally {
    isUploading.value = false;
  }
};

const createBook = async (book: NewBookV1) => {
  const { data: bookId } = await axios.post(`${config.value('booksContentRegistryUrl')}`, book);
  return bookId;
};

const startGeneration = async (language: Language, bookId: string) => {
  const data: CreateEpubRequest = {
    fileName: selectedFile.value!.name.replace('.csv', ''),
    language: language.languageCode,
    contentId: bookId,
    isbn: isbn.value,
  };
  await axios.post(`${config.value('epubGuideGeneratorUrl')}/`, data);
};

const { upload } = useUploadMedia();

const uploadCoverImage = async (bookId: string) => {
  await upload(bookId, selectedCover.value!, { subType: 'bookCover' });
};

const startCreateProcess = async () => {
  if (!selectedGuideLanguages.value.length || !selectedFile.value) {
    return;
  }
  try {
    const languageCodes: string[] = selectedGuideLanguages.value.map((lang) => lang.languageCode);
    await uploadCsv(languageCodes);
    await validateCSV();
    const generationPromises = selectedGuideLanguages.value.map(async (lang: Language) => {
      const book = {
        ...BOOK.value,
        ...getLMGBaseInfo(BOOK.value.title, lang, BOOK.value.accessRules.subscriptionType),
      };
      const { id } = await createBook(book);
      if (id) {
        await startGeneration(lang, id);
        if (selectedCover.value) {
          await uploadCoverImage(id);
        }
      } else {
        console.error(`Failed to create book for language: ${lang}`);
        eventBus.$emit('snackbar.open', `Failed to create book for language: ${lang}`);
      }
    });
    await Promise.all(generationPromises);
    eventBus.$emit(
      'snackbar.open',
      `Process started. Allow a few minutes for the EPUB(s) to be generated before checking the table below.`,
    );
  } catch (error) {
    console.error(error);
    if (axios.isAxiosError(error)) {
      if (error.response) {
        const data = error.response.data.error as ValidateCsvResponse;
        eventBus.$emit('snackbar.open', `CSV is not valid. Errors: ${data.errors!.join(', ')}`);
      }
    } else {
      eventBus.$emit('snackbar.open', 'Error starting generation');
    }
  } finally {
    isUploading.value = false;
    selectedGuideLanguages.value = [];
  }
};

return { __sfc: true,selectedGuideLanguages, pushToCR, selectedFile, selectedCover, isUploading, isbn, metadataFetched, NATIVE_LANGUAGE_GUIDE_CATEGORY_ID, BOOK, rules, hasService, availableSubscriptions, availableCategories, availableLanguages, shownCategories, fetchingMetadata, populateMetadata, fileChanged, coverChanged, validateCSV, uploadCsv, createBook, startGeneration, upload, uploadCoverImage, startCreateProcess, FileUploader, IsbnInput }
}

})